import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MasterdataService } from '../../services/master-data/masterdata.service';
import { SidebarService } from '../../shared/services/sidebar.service';
import { PageBreadcrumbComponent, BreadcrumbItem } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import {
  canNavigateNotification,
  getNotificationModuleIcon,
  getNotificationModuleName,
  isDeleteNotification,
  navigateNotificationToMap,
} from '../../shared/components/header/notification-dropdown/notification-navigation';

const NOTIFICATIONS_PAGE_SIZE = 20;

@Component({
  selector: 'app-notifications',
  imports: [CommonModule, RouterModule, TooltipModule, ToastModule, ConfirmDialogModule, PageBreadcrumbComponent],
  providers: [MessageService],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css',
})
export class NotificationsComponent implements OnInit {
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  breadcrumbs: BreadcrumbItem[] = [{ label: 'Notifications' }];

  notifications: any[] = [];
  total = 0;
  page = 1;
  readonly pageSize = NOTIFICATIONS_PAGE_SIZE;
  unreadCountFromApi: number | null = null;
  loading = false;
  notifyLoading: boolean = false
  markingAllRead = false;

  constructor(
    private masterdataService: MasterdataService,
    private sidebarService: SidebarService,
  ) { }

  ngOnInit(): void {
    this.load();
    this.sidebarService.updateNotify.subscribe((notify: boolean) => {
      if (notify) this.load();
    });
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  get unreadCount(): number {
    if (this.unreadCountFromApi !== null) return this.unreadCountFromApi;
    return this.notifications.filter((n: any) => !n.is_read).length;
  }

  getVisiblePages(): number[] {
    const maxVisible = 5;
    let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getStartRecord(): number {
    if (this.total === 0) return 0;
    return (this.page - 1) * this.pageSize + 1;
  }

  getEndRecord(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) return;
    this.page = page;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.notifyLoading = true;
    this.masterdataService.getNotificationsPaged(this.page, this.pageSize).subscribe({
      next: (res: any) => {
        this.notifications = res?.data ?? [];
        this.total = res?.count ?? 0;
        this.unreadCountFromApi = typeof res?.unread_count === 'number' ? res.unread_count : null;
        // Dropped rows (e.g. after delete-all) can leave the page empty — step back.
        if (this.notifications.length === 0 && this.total > 0 && this.page > 1) {
          this.page = Math.min(this.page - 1, Math.max(this.totalPages, 1));
          this.load();
          return;
        }
        this.loading = false;
        this.notifyLoading = false;
      },
      error: (err: any) => {
        this.loading = false;
        this.notifyLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          life: 4000,
          detail: err?.message || 'Failed to load notifications',
        });
      },
    });
  }

  private decrementUnread(): void {
    if (this.unreadCountFromApi !== null) {
      this.unreadCountFromApi = Math.max(0, this.unreadCountFromApi - 1);
    }
  }

  onRowClick(item: any): void {
    const shouldNavigate = !isDeleteNotification(item);
    if (!item.is_read) {
      this.masterdataService.updateNotification({ is_read: true }, item.id).subscribe({
        next: (res: any) => {
          const idx = this.notifications.findIndex((n: any) => n.id === item.id);
          if (idx !== -1) this.notifications[idx] = res;
          this.decrementUnread();
          this.sidebarService.updateNotify.next(true);
          if (shouldNavigate) navigateNotificationToMap(this.router, item);
        },
        error: (err: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            life: 4000,
            detail: err?.message || 'An Error Occured',
          });
        },
      });
    } else if (shouldNavigate) {
      navigateNotificationToMap(this.router, item);
    }
  }

  canNavigate(item: any): boolean {
    return canNavigateNotification(item);
  }

  getModuleName(moduleId: number | string): string {
    return getNotificationModuleName(moduleId);
  }

  getModuleIcon(moduleId: number | string): string {
    return getNotificationModuleIcon(moduleId);
  }

  markAllRead(): void {
    if (this.unreadCount === 0 || this.markingAllRead) return;
    this.markingAllRead = true;
    this.masterdataService.markAllNotificationsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((n: any) => ({ ...n, is_read: true }));
        this.unreadCountFromApi = 0;
        this.markingAllRead = false;
        this.sidebarService.updateNotify.next(true);
      },
      error: (err: any) => {
        this.markingAllRead = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          life: 4000,
          detail: err?.message || 'Failed to mark all as read',
        });
      },
    });
  }

  deleteNotification(item: any, event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'notifications-page',
      message: 'Delete this notification? This cannot be undone.',
      header: 'Delete notification',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptLabel: 'Delete',
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => this.confirmDeleteNotification(item),
    });
  }

  private confirmDeleteNotification(item: any): void {
    const wasUnread = !item?.is_read;
    this.masterdataService.deleteNotificationRow(item.id).subscribe({
      next: () => {
        this.notifications = this.notifications.filter((n: any) => n.id !== item.id);
        this.total = Math.max(0, this.total - 1);
        if (wasUnread) this.decrementUnread();
        this.sidebarService.updateNotify.next(true);
        if (this.notifications.length === 0 && this.total > 0 && this.page > 1) {
          this.page = this.page - 1;
        }
        this.load();
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          life: 4000,
          detail: err?.message || 'Failed to delete',
        });
      },
    });
  }

  deleteAllNotifications(): void {
    if (!this.notifications.length && this.total === 0) return;
    const count = this.total;
    this.confirmationService.confirm({
      key: 'notifications-page',
      message: `Delete all ${count} notification${count === 1 ? '' : 's'}? This cannot be undone.`,
      header: 'Delete all notifications',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptLabel: 'Delete all',
      acceptButtonProps: {
        label: 'Delete all',
        severity: 'danger'
      },
      accept: () => this.confirmDeleteAllNotifications(),
    });
  }

  private confirmDeleteAllNotifications(): void {
    this.masterdataService.deleteNotificationBulk().subscribe({
      next: () => {
        this.notifications = [];
        this.total = 0;
        this.page = 1;
        this.unreadCountFromApi = 0;
        this.sidebarService.updateNotify.next(true);
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          life: 4000,
          detail: err?.message || 'Failed to delete all',
        });
      },
    });
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
  }

  formatNotificationDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} | ${hours}:${minutes} ${ampm}`;
  }
}
